import { Group, Stack, Text, Textarea } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Clock3, Guitar } from "lucide-react";
import { useState } from "react";

import type { CatalogAudioModel, MusicMode } from "../../../api/generated/model";
import { AudioMusicQuote, AudioMusicRequest } from "../../../api/generated/zod";
import { ImageRefsSlot } from "../../../platform/media/ImageRefsSlot";
import { UploadState } from "../../../platform/media/media-upload";
import slots from "../../../platform/media/slots.module.css";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { UsageNotes } from "../../../ui/UsageNotes";
import { defaultOf, type SoundFormProps } from "./SoundEffectsForm";
import { SoundErrors, StartRow } from "./StartRow";
import { ToggleChip } from "./ToggleChip";
import { IMAGE_UPLOADING, useMusicImages } from "./use-music-images";
import { ROUTE, useSoundJob } from "./use-sound-job";

/** Live's words (`music/constants.ts`, capture 04). */
const PLACEHOLDER = "A cinematic orchestral soundtrack with an epic build-up.";
const TIPS = {
  model: "Select the model for music generation.",
  duration: "How long the generated track should be.",
  instrumental: "Generate the track without vocals.",
} as const;

/**
 * A model's starting length, live's `getDefaultMusicDurationValue`: its default, else its least,
 * for an exact length only; none where the model chooses the length.
 */
function startingLength(block: MusicMode): number | null {
  const { duration } = block;
  if (duration.control !== "exact_seconds") return null;
  return duration.default_seconds ?? duration.min_seconds ?? null;
}

/** The lengths a model offers: live's presets inside its range, for an exact length only. */
function lengthsOf(block: MusicMode): number[] {
  const { duration } = block;
  if (duration.control !== "exact_seconds") return [];
  const low = duration.min_seconds ?? 1;
  const high = duration.max_seconds ?? Number.POSITIVE_INFINITY;
  return (duration.presets ?? []).filter((each) => each >= low && each <= high);
}

/**
 * Live's Music composer (#235, capture 04, L01e to L01h): the prompt, then the model (live's list
 * with its makers), the length where the model takes one, the instrumental chip where it has
 * one, the usage notes, the count, the price and the start. Where the model takes images (Lyria),
 * live's "Image refs" above the prompt (#219, L01e): up to the model's most, sent in order.
 */
export function MusicForm({ models, above, onStarted, inline }: SoundFormProps) {
  const [model, setModel] = useState<CatalogAudioModel>(defaultOf(models));
  const block = model.music!;
  const [prompt, setPrompt] = useState("");
  const [seconds, setSeconds] = useState<number | null>(startingLength(block));
  const [instrumental, setInstrumental] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { teamId, brandId } = useParams({ from: ROUTE });
  // Live's moodboard: the model's most, none where it takes no images (the slot hides).
  const most = block.reference_images?.max ?? 0;
  const images = useMusicImages(teamId, brandId, most);
  const lengths = lengthsOf(block);
  // Live's `applyModelDefaults` (#598): another model starts at its own length, even where it
  // offers the one chosen; Instrumental goes off where it has none; only the images it takes stay.
  const choose = (id: string) => {
    const next = models.find((each) => each.id === id);
    if (next === undefined) return;
    setModel(next);
    setSeconds(startingLength(next.music!));
    if (!next.music!.force_instrumental) setInstrumental(false);
    images.keep(next.music!.reference_images?.max ?? 0);
  };
  const fields = {
    type: "audio.music" as const,
    model: model.id,
    ...(lengths.length === 0 || seconds === null ? {} : { duration_seconds: seconds }),
    ...(block.force_instrumental ? { force_instrumental: instrumental } : {}),
    ...(images.refs.length > 0 ? { reference_images: images.refs } : {}),
  };
  const quote = AudioMusicQuote.safeParse(fields);
  const written = prompt.trim();
  // Live's rule: no start while an image is still on its way up.
  const uploading = images.upload.pending;
  const request =
    written.length >= block.prompt_min_chars && !uploading
      ? AudioMusicRequest.safeParse({ ...fields, prompt: written })
      : undefined;
  // The price never reads the description: the quote sends none (as live's estimate).
  const job = useSoundJob(
    quote.success ? fields : undefined,
    request?.success === true ? request.data : undefined,
    written !== "",
    onStarted,
  );
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      summary={<span>Music</span>}
      header={
        most > 0 ? (
          <div className={slots.slots}>
            <ImageRefsSlot refs={images.slot} />
          </div>
        ) : undefined
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={12} wrap="nowrap">
            <ModelPicker
              models={models}
              value={model.id}
              onChange={choose}
              bolts={false}
              tip={TIPS.model}
            />
            {lengths.length === 0 || seconds === null ? null : (
              <Choice
                setting="Duration"
                icon={<Clock3 size={20} strokeWidth={1.5} />}
                chevron={false}
                width={96}
                tip={TIPS.duration}
                value={String(seconds)}
                options={lengths.map((each) => ({ value: String(each), label: `${each} sec` }))}
                onChange={(next) => setSeconds(Number(next))}
              />
            )}
            {block.force_instrumental ? (
              <ToggleChip
                setting="Instrumental"
                icon={<Guitar size={20} strokeWidth={1.5} aria-hidden />}
                on={instrumental}
                onChange={setInstrumental}
                tip={TIPS.instrumental}
              />
            ) : null}
            <UsageNotes notes={block.notes} />
          </Group>
          <StartRow job={job} />
        </Group>
      }
    >
      <Stack gap={4}>
        <Textarea
          variant="unstyled"
          aria-label="Prompt"
          placeholder={PLACEHOLDER}
          autosize
          minRows={1}
          maxRows={5}
          maxLength={model.prompt_max_chars}
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        <UploadState upload={images.upload} />
        {uploading ? (
          <Text size="sm" c="var(--cai-color-text-muted)">
            {IMAGE_UPLOADING}
          </Text>
        ) : null}
        <SoundErrors job={job} />
      </Stack>
      {images.dialog}
    </GlassPanel>
  );
}
